import type { RecommendedCoursePlanItem } from "@brookbush/types";

import CertificationModuleTitle from "./CertificationCarouselTitle";
import CertificationStudyPlanActionCards from "./CertificationStudyPlanActionCards";
import CardGrid from "~/components/CardGrid";
import { useCertification, useNccaExamCoachRecommendation } from "~/hooks";
import DashboardLayoutHeader from "~/layouts/DashboardLayout/DashboardLayoutHeader";
import { ACCREDITED_CPT_EXAM_STUDY_PLAN } from "~/lib/accredited-cpt-exam-study-plan";
import { toArray } from "~/lib/toArray";
import CoursesCard from "~/pages/courses/components/CoursesCard";

type CertificationStudyPlanProps = {
	coursePlan?: RecommendedCoursePlanItem[] | null;
};

const CertificationStudyPlan = ({
	coursePlan,
}: CertificationStudyPlanProps) => {
	const { certification } = useCertification();
	const { isCourseRecommended } = useNccaExamCoachRecommendation(
		certification?.slug,
	);

	const planItems = toArray(coursePlan);
	const modules = toArray(certification?.modules);

	const getCertificationModule = (slug: string) =>
		modules.find((module) => module.slug === slug);

	return (
		<div className="flex w-full flex-col">
			<DashboardLayoutHeader
				title={ACCREDITED_CPT_EXAM_STUDY_PLAN.pageTitle}
				description={ACCREDITED_CPT_EXAM_STUDY_PLAN.pageDescription}
			/>

			<CertificationStudyPlanActionCards />

			<div className="flex flex-col gap-6">
				{planItems.map((planItem, i) => (
					<div
						id={planItem.slug}
						key={planItem._key}
						className="flex laptop:scroll-m-12 scroll-m-20 flex-col gap-2 sm:gap-3"
					>
						{planItem.type === "core" ? (
							<CertificationModuleTitle
								href={`/certifications/${certification.slug}/modules/${planItem.slug}`}
								module={{
									...getCertificationModule(planItem.slug)!,
									title: `${i + 1}. ${
										getCertificationModule(planItem.slug)?.title
									}`,
								}}
							/>
						) : (
							<div className="flex items-center gap-2">
								<h2 className="group line-clamp-2 items-center font-medium phablet:font-semibold text-[1.2rem] leading-6 sm:text-2xl">
									{i + 1}. {planItem.title}
								</h2>

								<span className="relative ml-auto phablet:ml-0 flex items-center justify-center whitespace-nowrap rounded-full bg-purple-100 px-2.5 py-1 font-semibold phablet:text-sm text-[0.8rem] dark:bg-purple-300/30 dark:text-slate-50">
									Electives
								</span>
							</div>
						)}

						<CardGrid>
							{toArray(planItem.courses).map((course) => (
								<div className="card-grid-node-basis" key={course._id}>
									<CoursesCard
										{...course}
										isRecommended={isCourseRecommended(course._id)}
									/>
								</div>
							))}
						</CardGrid>
					</div>
				))}
			</div>
		</div>
	);
};

export default CertificationStudyPlan;
